import { useState, useEffect } from "react";
import { createBrowserRouter, RouterProvider, Route } from "react-router-dom";

import {
  AuthChangeRedirector,
  AnonymousRoute,
  AuthenticatedRoute,
} from "@sharpana/auth/AuthRoutingWrappers";

import Root from "./Root";
import Home from "./Home";

import { useConfig } from "@sharpana/auth/authHooks";
import { getAuthRoutes } from "@sharpana/auth/routes";
import { getDebugRoutes } from "./routes/debugRoutes";
import { SummarizationURLRequest } from "./pages/SummarizationURLRequest";
import { AddPlaylist } from "./pages/AddPlaylist";
import { MyPlaylists } from "./pages/MyPlaylists";
import ExtensionRequestSummary from "./pages/ExtensionRequestSummary";
import ResourceContentSummary from "./pages/ResourceContentSummary";
import { FetchAndShowPlaylistDetail } from "./pages/PlaylistDetail";
import { HomeFeed } from "./pages/HomeFeed";
import BookmarksFeed from "./pages/BookmarksFeed";
import Onboarding from "./pages/Onboarding";
import { SharpSprint } from "./pages/SharpSprint";
import { StartNewRequest } from "./pages/StartNewRequest";
import { SessionHistory } from "./pages/SessionHistory";
import Settings from "./pages/Settings";
import UserAccount from "./pages/UserAccount";
import Paywall from "./pages/Paywall";
import StripePostCheckout from "./pages/StripePostCheckout";
import RevcatPostCheckout from "./pages/RevcatPostCheckout";
import SupportPage from "./pages/SupportPage";

function createRouter(config: any) {
  const authRoutes = getAuthRoutes(config);
  const debugRoutes = getDebugRoutes(config);

  return createBrowserRouter([
    {
      path: "/",
      element: (
        <AuthChangeRedirector>
          <Root />
        </AuthChangeRedirector>
      ),
      children: [
        {
          path: "/",
          element: (
            <AuthenticatedRoute>
              <HomeFeed />
            </AuthenticatedRoute>
          ),
        },
        {
          path: "/summarization-request",
          element: (
            <AuthenticatedRoute>
              <StartNewRequest />
            </AuthenticatedRoute>
          ),
        },
        {
          path: "/feed",
          element: (
            <AuthenticatedRoute>
              <HomeFeed />
            </AuthenticatedRoute>
          ),
        },
        {
          path: "/sharp-sprint/*",
          element: (
            <AuthenticatedRoute>
              <SharpSprint />
            </AuthenticatedRoute>
          ),
        },
        {
          path: "/history",
          element: (
            <AuthenticatedRoute>
              <SessionHistory />
            </AuthenticatedRoute>
          ),
        },
        {
          path: "/playlist/:id",
          element: (
            <AuthenticatedRoute>
              <FetchAndShowPlaylistDetail />
            </AuthenticatedRoute>
          ),
        },
        {
          path: "/summary/:resourceContentId",
          element: (
              <ResourceContentSummary />
          ),
        },
        {
          path: "/ext-request-summary/:tabId",
          element: (
            <AuthenticatedRoute>
              <ExtensionRequestSummary />
            </AuthenticatedRoute>
          ),
        },
        {
          path: "/onboarding/*",
          element: (
            <AuthenticatedRoute>
              <Onboarding />
            </AuthenticatedRoute>
          ),
        },
        {
          path: "/bookmarks",
          element: (
            <AuthenticatedRoute>
              <BookmarksFeed />
            </AuthenticatedRoute>
          ),
        },
        {
          path: "/settings",
          element: (
            <AuthenticatedRoute>
              <Settings />
            </AuthenticatedRoute>
          ),
        },
        {
          path: "/account",
          element: (
            <AuthenticatedRoute>
              <UserAccount />
            </AuthenticatedRoute>
          ),
        },
        {
          path: "/support",
          element: (
            <AuthenticatedRoute>
              <SupportPage />
            </AuthenticatedRoute>
          ),
        },
        {
          path: "/purchase",
          element: (
            <AuthenticatedRoute>
              <Paywall />
            </AuthenticatedRoute>
          ),
        },
        {
          path: "/stripe-post-checkout",
          element: (
            <AuthenticatedRoute>
              <StripePostCheckout />
            </AuthenticatedRoute>
          ),
        },
        {
          path: "/revcat-post-checkout",
          element: (
            <AuthenticatedRoute>
              <RevcatPostCheckout />
            </AuthenticatedRoute>
          ),
        },
        ...debugRoutes,
        ...authRoutes,
      ],
    },
  ]);
}

export default function Router() {
  // If we create the router globally, the loaders of the routes already trigger
  // even before the <AuthContext/> trigger the initial loading of the auth.
  // state.
  const [router, setRouter] = useState<any>(null);
  const config = useConfig();
  useEffect(() => {
    setRouter(createRouter(config));
  }, [config]);
  return router ? <RouterProvider router={router} /> : null;
}
